import { useState, useEffect } from 'react';
import PropertiesAPI from '../PropertiesAPI';
import { TRANSFER_TARGET_FIELD_NAMES } from './constants';

const { COMPANY, LEASING_TEAM, MANAGER } = TRANSFER_TARGET_FIELD_NAMES;

export const FETCH_LEASING_TEAMS_ERROR =
  'Unable to fetch leasing teams. Please try again later.';
export const FETCH_MANAGERS_ERROR =
  'Unable to fetch managers. Please try again later.';
export const COMPANY_REQUIRED_ERROR = 'Company is required';
export const LEASING_TEAM_REQUIRED_ERROR = 'Leasing team is required';
export const MANAGER_REQUIRED_ERROR = 'New Property Owner is required';

const useTransferTarget = ({
  fetchCompanies,
  handleTransferProperty,
  setErrorMessage,
  setIsLoading,
  shouldFetchCompanies,
}) => {
  const [company, setCompany] = useState(null);
  const [leasingTeam, setLeasingTeam] = useState({});
  const [leasingTeams, setLeasingTeams] = useState(null);
  const [manager, setManager] = useState({});
  const [managers, setManagers] = useState(null);
  const [validationErrorMessages, setValidationErrorMessages] = useState({});

  useEffect(() => {
    if (shouldFetchCompanies) {
      fetchCompanies();
    }
  }, [fetchCompanies, shouldFetchCompanies]);

  const fetchLeasingTeams = async (companyId) => {
    setIsLoading(true);
    try {
      const leasingTeams =
        await PropertiesAPI.getLeasingTeamsByCompany(companyId);
      setLeasingTeams(leasingTeams);
    } catch {
      setLeasingTeams([]);
      setErrorMessage(FETCH_LEASING_TEAMS_ERROR);
    }
    setIsLoading(false);
  };

  const fetchManagers = async (leasingTeamId) => {
    setIsLoading(true);
    try {
      const managers =
        await PropertiesAPI.getManagersByLeasingTeam(leasingTeamId);
      setManagers(managers);
    } catch {
      setManagers([]);
      setErrorMessage(FETCH_MANAGERS_ERROR);
    }
    setIsLoading(false);
  };

  const handleCompanyOnChange = (_e, companyOption) => {
    setCompany(companyOption);
    setLeasingTeam({});
    setLeasingTeams(null);
    setManager({});
    setManagers(null);
    fetchLeasingTeams(companyOption.companyId);
  };

  const handleLeasingTeamOnChange = (value) => {
    setLeasingTeam(value);
    setManager({});
    setManagers(null);
    fetchManagers(value.leasingTeamId);
  };

  const handleManagerOnChange = (value) => {
    setManager(value);
  };

  const handleOnFocus = (name) => () => {
    if (validationErrorMessages[name]) {
      setValidationErrorMessages((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors = {};

    if (!company?.companyId) {
      errors[COMPANY] = COMPANY_REQUIRED_ERROR;
    }

    if (leasingTeams?.length > 0 && !leasingTeam.leasingTeamId) {
      errors[LEASING_TEAM] = LEASING_TEAM_REQUIRED_ERROR;
    }

    if (managers?.length > 0 && !manager.value) {
      errors[MANAGER] = MANAGER_REQUIRED_ERROR;
    }

    setValidationErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTransferOnClick = () => {
    const isValid = validate();

    if (isValid) {
      handleTransferProperty({
        leasingTeamPublicId: leasingTeam.leasingTeamPublicId,
        managerUsername: manager.value ?? null,
      });
    }
  };

  const isTransferDisabled =
    !company?.companyId ||
    (leasingTeams !== null && leasingTeams.length === 0) ||
    (managers !== null && managers.length === 0);

  return {
    company,
    handleCompanyOnChange,
    handleLeasingTeamOnChange,
    handleOnFocus,
    handleTransferOnClick,
    handleManagerOnChange,
    isTransferDisabled,
    leasingTeam,
    leasingTeams,
    manager,
    managers,
    validationErrorMessages,
  };
};

export default useTransferTarget;
